<template>
  <div>
    <v-dialog
      v-model="show"
      width="720px"
    >
      <v-card color="#f4f6f9">
        <v-card-title class="device-messages-title">
          <span>Device Messages</span>
          <span
            v-if="macAddress"
            class="device-messages-mac"
          >{{ macAddress }}</span>
        </v-card-title>

        <v-card-text>
          <v-row class="ma-2">
            <v-text-field
              v-model="message"
              label="Message"
              outlined
              class="mr-2"
              :disabled="sending"
              @keyup.enter="sendMessage()"
            />
            <v-btn
              color="primary"
              x-large
              :loading="sending"
              :disabled="!message.trim()"
              @click="sendMessage()"
            >
              Send
            </v-btn>
          </v-row>

          <div class="mx-2 mb-3">
            <command-expiry-picker
              v-model="expiry"
              :default-info="messageExpiryInfo"
              :disabled="sending"
            />
          </div>

          <div class="device-messages-toolbar mb-3">
            <div class="device-messages-counts">
              <v-chip
                small
                outlined
                color="primary"
              >
                {{ total }} total
              </v-chip>
              <v-chip
                small
                color="success"
                text-color="white"
              >
                {{ readCount }} read
              </v-chip>
              <v-chip
                small
                :color="pendingCount ? 'orange' : 'grey lighten-1'"
                text-color="white"
              >
                {{ pendingCount }} pending
              </v-chip>
            </div>
            <div class="device-messages-filter">
              <v-btn-toggle
                v-model="readFilter"
                dense
                mandatory
                color="primary"
              >
                <v-btn
                  small
                  value="all"
                >
                  All
                </v-btn>
                <v-btn
                  small
                  value="read"
                >
                  Read
                </v-btn>
                <v-btn
                  small
                  value="pending"
                >
                  Pending
                </v-btn>
              </v-btn-toggle>
              <v-btn
                icon
                small
                title="Refresh"
                :loading="loading"
                @click="getMessages()"
              >
                <v-icon small>
                  mdi-refresh
                </v-icon>
              </v-btn>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :items="items"
            :options.sync="options"
            :loading="loading"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            no-data-text="No messages for this device."
          >
            <template v-slot:[`item.description`]="{ item }">
              <div class="device-messages-text">
                {{ item.description || '-' }}
              </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                small
                :color="item.isRead ? 'success' : 'orange'"
                text-color="white"
              >
                <v-icon
                  x-small
                  left
                >
                  {{ item.isRead ? 'mdi-check-all' : 'mdi-clock-outline' }}
                </v-icon>
                {{ item.status || (item.isRead ? 'Read' : 'Pending') }}
              </v-chip>
            </template>
            <template v-slot:[`item.sentAt`]="{ item }">
              <span class="device-messages-date">{{ shortDateTime(item.sentAt) }}</span>
            </template>
            <template v-slot:[`item.receivedAt`]="{ item }">
              <span class="device-messages-date">{{ item.isRead ? shortDateTime(item.receivedAt) : 'Not read yet' }}</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="close"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import CommandExpiryPicker from './CommandExpiryPicker.vue'
  import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'
  import { buildTtlMinutes, normalizeCatalog } from '@/util/commandCatalog'

  const toastOptions = { position: 'topRight' }

  export default {
    name: 'DeviceMessageDialog',
    components: {
      CommandExpiryPicker,
    },
    props: ['value', 'deviceId'],
    data: () => ({
      message: '',
      items: [],
      total: 0,
      readCount: 0,
      pendingCount: 0,
      macAddress: '',
      readFilter: 'all',
      options: {},
      loading: false,
      sending: false,
      headers: [
        {
          text: 'Message',
          sortable: false,
          value: 'description',
        },
        {
          text: 'Read Status',
          sortable: false,
          value: 'status',
        },
        {
          text: 'Sent At',
          sortable: false,
          value: 'sentAt',
        },
        {
          text: 'Read At',
          sortable: false,
          value: 'receivedAt',
        },
      ],
      expiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      commandCatalog: null,
    }),
    computed: {
      messageExpiryInfo () {
        const catalog = this.commandCatalog
        return (catalog && catalog.expiryByType && catalog.expiryByType.SendMessageToDevice) || null
      },
      show: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
    },
    watch: {
      show (val) {
        if (val) {
          this.expiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
          this.loadCommandCatalog()
          this.getMessages()
        } else {
          this.close()
        }
      },
      deviceId () {
        this.getMessages()
      },
      readFilter () {
        this.getMessages()
      },
    },

    methods: {
      // Expiry defaults are published per command type by the group catalog.
      async loadCommandCatalog () {
        if (this.commandCatalog) {
          return
        }
        try {
          const response = await DeviceGroupsDataService.getCommandTypes()
          this.commandCatalog = normalizeCatalog(response.data)
        } catch (error) {
          console.error('Error loading command catalog:', error)
          this.commandCatalog = normalizeCatalog(null)
        }
      },
      // Reads a value the API may return as camelCase or PascalCase.
      pick (source, ...keys) {
        for (const key of keys) {
          const pascal = key.charAt(0).toUpperCase() + key.slice(1)
          if (source?.[key] !== undefined && source?.[key] !== null) {
            return source[key]
          }
          if (source?.[pascal] !== undefined && source?.[pascal] !== null) {
            return source[pascal]
          }
        }
        return undefined
      },
      async getMessages () {
        if (!this.deviceId) {
          return
        }

        this.loading = true
        try {
          // isRead is an optional filter; omit it to list every message.
          const params = {}
          if (this.readFilter === 'read') {
            params.isRead = true
          } else if (this.readFilter === 'pending') {
            params.isRead = false
          }

          const response = await this.$http.get(
            `Message/GetDeviceMessages/${encodeURIComponent(this.deviceId)}`,
            { params },
          )
          this.applyMessages(response.data)
        } catch (error) {
          this.items = []
          this.total = 0
          this.readCount = 0
          this.pendingCount = 0
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load device messages.',
            'Error',
            toastOptions,
          )
        } finally {
          this.loading = false
        }
      },
      applyMessages (raw) {
        const root = (raw && !Array.isArray(raw) && this.pick(raw, 'data')) || raw
        const list = Array.isArray(root)
          ? root
          : (this.pick(root, 'messages', 'items', 'data') || [])

        this.items = (Array.isArray(list) ? list : [])
          .filter(entry => entry && typeof entry === 'object')
          .map((entry) => {
            const isRead = this.pick(entry, 'isRead') === true
            const receivedAt = this.pick(entry, 'receivedAt') || ''
            return {
              ...entry,
              description: this.pick(entry, 'description', 'message', 'text') || '',
              // ReceivedAt is null until the device reports the message as read.
              isRead: isRead || Boolean(receivedAt),
              receivedAt,
              sentAt: this.pick(entry, 'sentAt') || '',
              status: this.pick(entry, 'status') || '',
            }
          })

        this.macAddress = String(this.pick(root, 'macAddress', 'deviceMacAddress') || '')
        this.total = Number(this.pick(root, 'total', 'totalCount') ?? this.items.length) || this.items.length
        this.readCount = Number(
          this.pick(root, 'readCount') ?? this.items.filter(item => item.isRead).length,
        ) || 0
        this.pendingCount = Number(
          this.pick(root, 'pendingCount') ?? this.items.filter(item => !item.isRead).length,
        ) || 0
      },
      shortDateTime (value) {
        if (!value) {
          return '-'
        }
        // Backend sends naive UTC timestamps; render them in local time.
        const parsed = moment.utc(value)
        return parsed.isValid() && parsed.year() > 2010
          ? parsed.local().format('DD MMM YYYY, h:mm a')
          : '-'
      },
      async sendMessage () {
        const text = this.message.trim()
        if (!text) {
          return
        }

        this.sending = true
        try {
          const params = { deviceId: this.deviceId, message: text }
          const ttlMinutes = buildTtlMinutes(this.expiry)
          if (ttlMinutes) {
            params.ttlMinutes = ttlMinutes
          }

          const response = await this.$http.get('message/sentMessageToDevice', { params })

          if (response.data.success) {
            this.message = ''
            this.$toast.success(response.data.message, 'Success', toastOptions)
          } else {
            this.$toast.error(response.data.message, 'Error', toastOptions)
          }
          await this.getMessages()
        } finally {
          this.sending = false
        }
      },
      close () {
        this.show = false
      },
    },
  }
</script>

<style scoped>
  .device-messages-title {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  .device-messages-mac {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.55);
  }

  .device-messages-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  .device-messages-counts,
  .device-messages-filter {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .device-messages-text {
    white-space: normal;
    word-break: break-word;
    max-width: 260px;
  }

  .device-messages-date {
    white-space: nowrap;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
  }
</style>
